import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import { Video, Plus, ExternalLink, Trash2, Mail, Phone, Calendar, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  paid: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-800',
  pending_payment: 'bg-orange-100 text-orange-800',
};

const STATUS_OPTIONS = [
  { value: 'paid', label: 'Paid (awaiting work)' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const VideoOrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const q = filter === 'all' ? '' : `?status=${filter}`;
      const res = await axios.get(`${API_URL}/api/admin/video-orders${q}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data.orders || []);
    } catch {
      toast.error('Failed to load orders');
    }
    setLoading(false);
  };

  useEffect(() => { fetchOrders(); }, [filter]);

  const openOrder = (order) => {
    setSelected(order);
    setAdminNotes(order.admin_notes || '');
    setFileUrl('');
    setFileName('');
  };

  const updateStatus = async (newStatus) => {
    if (!selected) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API_URL}/api/admin/video-orders/${selected.id}`, {
        status: newStatus,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(res.data.order);
      toast.success('Status updated');
      fetchOrders();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API_URL}/api/admin/video-orders/${selected.id}`, {
        admin_notes: adminNotes,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(res.data.order);
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    }
  };

  const addFile = async () => {
    if (!fileUrl.trim()) return toast.error('File URL required');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/admin/video-orders/${selected.id}/add-file`, {
        file_url: fileUrl.trim(),
        file_name: fileName.trim() || 'video.mp4',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(res.data.order);
      setFileUrl('');
      setFileName('');
      toast.success('Delivered file added');
      fetchOrders();
    } catch {
      toast.error('Failed to add file');
    }
  };

  const deleteOrder = async () => {
    if (!selected) return;
    if (!window.confirm('Delete this order? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/video-orders/${selected.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelected(null);
      toast.success('Order deleted');
      fetchOrders();
    } catch {
      toast.error('Failed to delete order');
    }
  };

  return (
    <div className="space-y-4" data-testid="video-orders-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-lg uppercase tracking-tight">Video Orders</h3>
          <p className="text-sm text-muted-foreground">Custom video edit requests from customers</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orders</SelectItem>
            <SelectItem value="paid">Paid (awaiting)</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Video className="w-12 h-12 mx-auto opacity-40 mb-2" />
          <p>No video orders yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {orders.map(o => (
            <Card key={o.id} className="cursor-pointer hover:bg-muted/40 transition" onClick={() => openOrder(o)} data-testid={`order-card-${o.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold uppercase">{o.event_name}</span>
                      <Badge className={STATUS_COLORS[o.status] || ''}>{o.status.replace('_', ' ')}</Badge>
                      {o.is_subscriber_at_purchase && <Badge variant="outline" className="text-primary">Sub</Badge>}
                    </div>
                    <p className="text-sm text-foreground mt-1">
                      <User className="w-3 h-3 inline mr-1" />
                      {o.customer_name} · {o.customer_email}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {o.num_videos} video{o.num_videos !== 1 ? 's' : ''}
                      {o.rider_team_name && ` · Rider: ${o.rider_team_name}`}
                      {o.run_number && ` · Run: ${o.run_number}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-display font-bold text-lg">${o.total.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-tight">
                  Video Order Detail
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5">
                <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h4 className="font-display font-bold text-lg">{selected.event_name}</h4>
                    <Badge className={STATUS_COLORS[selected.status] || ''}>{selected.status.replace('_', ' ')}</Badge>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-muted-foreground" /> {selected.customer_name}</div>
                    <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-muted-foreground" /> {selected.customer_email}</div>
                    {selected.customer_phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-muted-foreground" /> {selected.customer_phone}</div>}
                    <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-muted-foreground" /> {new Date(selected.created_at).toLocaleString()}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Videos</Label>
                    <p className="font-bold">{selected.num_videos}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Total Paid</Label>
                    <p className="font-bold text-primary">${selected.total.toFixed(2)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Rider/Team</Label>
                    <p className="font-medium">{selected.rider_team_name || '—'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Horse</Label>
                    <p className="font-medium">{selected.horse_name || '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-muted-foreground">Run / Draw #</Label>
                    <p className="font-medium">{selected.run_number || '—'}</p>
                  </div>
                </div>

                {selected.notes && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Customer Notes</Label>
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">{selected.notes}</div>
                  </div>
                )}

                {/* Status update */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Update Status</Label>
                  <div className="flex gap-2 flex-wrap">
                    {STATUS_OPTIONS.map(opt => (
                      <Button
                        key={opt.value}
                        size="sm"
                        variant={selected.status === opt.value ? 'default' : 'outline'}
                        onClick={() => updateStatus(opt.value)}
                        data-testid={`status-btn-${opt.value}`}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Delivered files */}
                <div>
                  <Label className="text-xs text-muted-foreground">Delivered Files ({(selected.delivered_files || []).length})</Label>
                  <div className="space-y-2 mt-2">
                    {(selected.delivered_files || []).map((f, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 p-2 bg-muted/40 rounded">
                        <span className="text-sm truncate flex-1">{f.name}</span>
                        <a href={f.url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> Open
                        </a>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 grid sm:grid-cols-3 gap-2">
                    <Input
                      placeholder="File URL (S3 or external)"
                      value={fileUrl}
                      onChange={(e) => setFileUrl(e.target.value)}
                      className="sm:col-span-2"
                      data-testid="file-url-input"
                    />
                    <Input
                      placeholder="File name"
                      value={fileName}
                      onChange={(e) => setFileName(e.target.value)}
                      data-testid="file-name-input"
                    />
                  </div>
                  <Button size="sm" onClick={addFile} className="mt-2" data-testid="add-file-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Delivered File
                  </Button>
                </div>

                {/* Admin notes */}
                <div>
                  <Label htmlFor="admin-notes" className="text-xs text-muted-foreground">Admin Notes (private)</Label>
                  <Textarea
                    id="admin-notes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                    placeholder="Internal notes about this order…"
                    data-testid="admin-notes"
                  />
                  <Button size="sm" variant="outline" onClick={saveNotes} className="mt-2">Save Notes</Button>
                </div>
              </div>

              <DialogFooter className="flex justify-between">
                <Button variant="destructive" size="sm" onClick={deleteOrder} data-testid="delete-order-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
                <Button onClick={() => setSelected(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoOrdersTab;
